import React, { useCallback, useEffect, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import debounce from "lodash/debounce";
import useApi from "../hook/useApi";
import { BASE_URL } from "../utils/constant";
import Loader from "./Loader/Loader";
import { useAuth } from "../context/AuthContext";
import { ValidateInputs } from "../utils/helper";

// Helper: resolve image URLs - prepend BASE_URL for backend-stored paths
const resolveImageUrl = (url) => {
  if (!url) return url;
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  if (url.startsWith("/uploads/")) return `${BASE_URL}${url}`;
  return url;
};

// Helper: validate password based on strength rules
const validatePassword = (password) => {
  if (password.length < 8) {
    return "Password must be at least 8 characters long";
  }
  if (password.length > 128) {
    return "Password cannot exceed 128 characters";
  }
  if (!/[A-Z]/.test(password)) {
    return "Password must contain at least one uppercase letter";
  }
  if (!/[a-z]/.test(password)) {
    return "Password must contain at least one lowercase letter";
  }
  if (!/[0-9]/.test(password)) {
    return "Password must contain at least one number";
  }
  if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
    return "Password must contain at least one special character";
  }
  return null;
};

const WhiteLabelsLayer = () => {
  const { AsyncGetApiCall, AsyncPostApiCall, AsyncPatchApiCall } = useApi();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [errors, setErrors] = useState({});

  // Modal States
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);

  // Password Visibility States
  const [showCreatePassword, setShowCreatePassword] = useState(false);
  const [showEditPassword, setShowEditPassword] = useState(false);
  const [showEditConfirmPassword, setShowEditConfirmPassword] = useState(false);
  const { profile } = useAuth()

  // Form States
  const [createForm, setCreateForm] = useState({
    subdomain: "",
    adminDomain: "",
    brokerDomain: "",
    userDomain: "",
    appName: "",
    standardLogoUrl: "",
    whiteLogoUrl: "",
    faviconUrl: "",
    primaryColor: "#0064FF",
    adminName: "",
    adminEmail: "",
    adminPassword: "",
    adminContactNumber: "",
    standardLogo: null,
    whiteLogo: null,
    favicon: null,
  });

  const [editForm, setEditForm] = useState({
    id: "",
    adminDomain: "",
    brokerDomain: "",
    userDomain: "",
    appName: "",
    standardLogoUrl: "",
    whiteLogoUrl: "",
    faviconUrl: "",
    primaryColor: "",
    isActive: true,
    standardLogo: null,
    whiteLogo: null,
    favicon: null,
    adminName: "",
    adminEmail: "",
    adminContactNumber: "",
    adminPassword: "",
    confirmAdminPassword: "",
  });

  const fetchWhiteLabels = async () => {
    setLoading(true);
    try {
      const response = await AsyncGetApiCall("/white-label");
      if (Array.isArray(response)) {
        setData(response);
        setFilteredData(response);
      } else if (response?.success && Array.isArray(response.data)) {
        setData(response.data);
        setFilteredData(response.data);
      } else {
        setData([]);
        setFilteredData([]);
      }
    } catch (error) {
      toast.error("Failed to load White Labels");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWhiteLabels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const debouncedSearch = useCallback(
    debounce((term) => {
      if (!term.trim()) {
        setFilteredData(data);
        return;
      }
      const lowerTerm = term.toLowerCase();
      const filtered = data.filter(
        (item) =>
          item.adminDomain?.toLowerCase().includes(lowerTerm) ||
          item.brokerDomain?.toLowerCase().includes(lowerTerm) ||
          item.userDomain?.toLowerCase().includes(lowerTerm) ||
          item.branding?.appName?.toLowerCase().includes(lowerTerm) ||
          item.adminId?.name?.toLowerCase().includes(lowerTerm) ||
          item.adminId?.email?.toLowerCase().includes(lowerTerm)
      );
      setFilteredData(filtered);
    }, 300),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data]
  );

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    debouncedSearch(e.target.value);
  };

  const handleToggleStatus = async (id, currentStatus) => {
    const { isConfirmed } = await Swal.fire({
      title: "Confirm Status Change?",
      text: `Are you sure you want to ${currentStatus ? "deactivate" : "activate"} this White Label tenant?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, toggle status",
      width: '350px',
      customClass: {
        title: 'fs-5 mb-0',
        htmlContainer: 'text-sm text-secondary'
      }
    });

    if (!isConfirmed) return;

    setLoading(true);
    try {
      const response = await AsyncPatchApiCall(`/white-label/${id}/toggle`);
      if (response?.success) {
        toast.success(response.message || "Status updated successfully");
        fetchWhiteLabels();
      } else {
        toast.error(response?.message || "Failed to toggle status");
      }
    } catch (error) {
      toast.error("An error occurred while toggling status");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    if (!createForm.adminDomain || !createForm.brokerDomain || !createForm.userDomain || !createForm.adminName || !createForm.adminEmail || !createForm.adminPassword) {
      toast.error("Please fill in all required fields.");
      return;
    }

    const passwordError = ValidateInputs("password", createForm.adminPassword, errors);
    if (passwordError?.password) {
      toast.error(passwordError?.password);
      return;
    }

    setLoading(true);
    try {
      const payload = new FormData();
      payload.append("adminDomain", createForm.adminDomain.trim());
      payload.append("brokerDomain", createForm.brokerDomain.trim());
      payload.append("userDomain", createForm.userDomain.trim());
      payload.append("adminName", createForm.adminName.trim());
      payload.append("adminEmail", createForm.adminEmail.trim());
      payload.append("adminPassword", createForm.adminPassword);
      if (createForm.adminContactNumber) payload.append("adminContactNumber", createForm.adminContactNumber.trim());

      const branding = {};
      if (createForm.appName) branding.appName = createForm.appName.trim();
      if (createForm.primaryColor) branding.primaryColor = createForm.primaryColor.trim();
      payload.append("branding", JSON.stringify(branding));

      if (createForm.standardLogo instanceof File) payload.append("standardLogo", createForm.standardLogo);
      if (createForm.whiteLogo instanceof File) payload.append("whiteLogo", createForm.whiteLogo);
      if (createForm.favicon instanceof File) payload.append("favicon", createForm.favicon);

      const response = await AsyncPostApiCall("/white-label", payload);
      if (response?.errors) {
        toast.error(Array.isArray(response.errors) ? response.errors[0] : response.errors);
      } else {
        toast.success("White Label created successfully");
        setIsCreateOpen(false);
        setCreateForm({
          subdomain: "",
          adminDomain: "",
          brokerDomain: "",
          userDomain: "",
          appName: "",
          standardLogoUrl: "",
          whiteLogoUrl: "",
          faviconUrl: "",
          primaryColor: "#0064FF",
          adminName: "",
          adminEmail: "",
          adminPassword: "",
          adminContactNumber: "",
          standardLogo: null,
          whiteLogo: null,
          favicon: null,
        });
        fetchWhiteLabels();
      }
    } catch (error) {
      toast.error("Failed to create White Label");
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setCreateForm({
      subdomain: "",
      adminDomain: "",
      brokerDomain: "",
      userDomain: "",
      appName: "",
      standardLogoUrl: "",
      whiteLogoUrl: "",
      faviconUrl: "",
      primaryColor: "#0064FF",
      adminName: "",
      adminEmail: "",
      adminPassword: "",
      adminContactNumber: "",
      standardLogo: null,
      whiteLogo: null,
      favicon: null,
    });
    // Reset file input elements if any
    const fileInputs = document.querySelectorAll('input[type="file"]');
    fileInputs.forEach(input => {
      input.value = "";
    });
    setIsCreateOpen(true);
  };

  const openEditModal = (item) => {
    setEditForm({
      id: item._id,
      adminDomain: item.adminDomain || "",
      brokerDomain: item.brokerDomain || "",
      userDomain: item.userDomain || "",
      appName: item.branding?.appName || "",
      standardLogoUrl: item.branding?.standardLogoUrl || "",
      whiteLogoUrl: item.branding?.whiteLogoUrl || "",
      faviconUrl: item.branding?.faviconUrl || "",
      primaryColor: item.branding?.primaryColor || "#0064FF",
      isActive: item.isActive,
      adminName: item.adminId?.name || "",
      adminEmail: item.adminId?.email || "",
      adminContactNumber: item.adminId?.contactNumber || "",
      adminPassword: "",
      confirmAdminPassword: "",
      standardLogo: null,
      whiteLogo: null,
      favicon: null,
      isMaster: !item.adminId,
    });
    setIsEditOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editForm.adminDomain || !editForm.brokerDomain || !editForm.userDomain || !editForm.appName) {
      toast.error("Please fill in all required fields.");
      return;
    }
    if (!editForm.isMaster && editForm.adminPassword) {
      const passwordError = validatePassword(editForm.adminPassword);
      if (passwordError) {
        toast.error(passwordError);
        return;
      }
      if (editForm.adminPassword !== editForm.confirmAdminPassword) {
        toast.error("Passwords do not match");
        return;
      }
    }
    setLoading(true);
    try {
      const payload = new FormData();
      if (editForm.adminDomain) payload.append("adminDomain", editForm.adminDomain.trim());
      if (editForm.brokerDomain) payload.append("brokerDomain", editForm.brokerDomain.trim());
      if (editForm.userDomain) payload.append("userDomain", editForm.userDomain.trim());
      if (editForm.adminName) payload.append("adminName", editForm.adminName.trim());
      if (editForm.adminEmail) payload.append("adminEmail", editForm.adminEmail.trim());
      if (editForm.adminContactNumber) payload.append("adminContactNumber", editForm.adminContactNumber.trim());
      if (editForm.adminPassword) payload.append("adminPassword", editForm.adminPassword);
      payload.append("isActive", editForm.isActive);

      const branding = {};
      if (editForm.appName) branding.appName = editForm.appName.trim();
      if (editForm.primaryColor) branding.primaryColor = editForm.primaryColor.trim();
      payload.append("branding", JSON.stringify(branding));

      if (editForm.standardLogo instanceof File) payload.append("standardLogo", editForm.standardLogo);
      if (editForm.whiteLogo instanceof File) payload.append("whiteLogo", editForm.whiteLogo);
      if (editForm.favicon instanceof File) payload.append("favicon", editForm.favicon);

      const response = await AsyncPatchApiCall(`/white-label/${editForm.id}`, payload);
      if (response?.success) {
        toast.success("White Label updated successfully");
        setIsEditOpen(false);
        fetchWhiteLabels();
      } else {
        toast.error(response?.message || "Update failed");
      }
    } catch (error) {
      toast.error("Failed to update White Label");
    } finally {
      setLoading(false);
    }
  };

  const isAdminHost = (item) => {
    return item?._id === profile?.whiteLabelId
  };

  return (
    <>
      {loading && <Loader />}
      <div className="card h-100 p-0 radius-12">
        {/* Header Controls */}
        <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
          <div className="d-flex align-items-center justify-content-between gap-3 w-100">
            <div className="d-flex align-items-center gap-3">
              <div className="position-relative">
                <input
                  type="text"
                  className="form-control bg-base h-40-px"
                  style={{ width: "320px", paddingLeft: "35px" }}
                  placeholder="Search White Labels..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                />
                <Icon
                  icon="ion:search-outline"
                  className="position-absolute"
                  style={{ left: "12px", top: "50%", transform: "translateY(-50%)", color: "#94a3b8" }}
                />
              </div>
            </div>
            <div>
              <button
                type="button"
                className="btn btn-primary radius-8 px-16 py-9 d-flex align-items-center gap-2"
                onClick={openCreateModal}
              >
                <Icon icon="solar:add-circle-bold" className="text-xl" />
                Add White Label
              </button>
            </div>
          </div>
        </div>

        {/* Content Body */}
        <div className="card-body p-24">
          <div className="table-responsive scroll-sm">
            <table className="table bordered-table sm-table mb-0">
              <thead>
                <tr>
                  <th scope="col">S.No</th>
                  <th scope="col">Admin Domain</th>
                  <th scope="col">Broker Domain</th>
                  <th scope="col">User Domain</th>
                  <th scope="col">App Name</th>
                  {/* <th scope="col">Primary Color</th> */}
                  <th scope="col">Admin User</th>
                  <th scope="col">Status</th>
                  <th scope="col" className="text-center">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredData.length > 0 ? (
                  filteredData?.map((item, index) => (
                    <tr key={item._id}>
                      <td>{index + 1}</td>
                      <td>
                        <span className="text-md mb-0 fw-semibold text-primary-600">
                          {item.adminDomain}
                        </span>
                      </td>
                      <td>
                        <span className="text-md mb-0 fw-semibold text-primary-600">
                          {item.brokerDomain}
                        </span>
                      </td>
                      <td>
                        <span className="text-md mb-0 fw-semibold text-primary-600">
                          {item.userDomain}
                        </span>
                      </td>
                      <td>
                        <span className="text-md mb-0 fw-normal text-secondary-light">
                          {item.branding?.appName || "N/A"}
                        </span>
                      </td>
                      {/* <td>
                        <div className="d-flex align-items-center gap-2">
                          <span
                            className="d-inline-block rounded-circle"
                            style={{
                              width: "16px",
                              height: "16px",
                              backgroundColor: item.branding?.primaryColor || "#000",
                              border: "1px solid #cbd5e1",
                            }}
                          />
                          <span className="text-md font-monospace text-secondary-light">
                            {item.branding?.primaryColor || "#0064FF"}
                          </span>
                        </div>
                      </td> */}
                      <td>
                        <div className="d-flex flex-column">
                          <span className="text-sm fw-medium text-secondary-light">
                            {item.adminId?.name || "N/A"}
                          </span>
                          <span className="text-xs text-secondary-light opacity-75">
                            {item.adminId?.email || "N/A"}
                          </span>
                        </div>
                      </td>
                      <td>
                        {isAdminHost(item) ? "Active" : (
                          <span
                            className={`badge ${item.isActive
                              ? "bg-success-focus text-success-600"
                              : "bg-danger-focus text-danger-600"
                              } ${item.adminId ? "cursor-pointer" : ""} border px-12 py-6 radius-4 fw-medium text-xs`}
                            onClick={() => {
                              if (item.adminId) {
                                handleToggleStatus(item._id, item.isActive);
                              }
                            }}
                          >
                            {item.isActive ? "Active" : "Inactive"}
                          </span>
                        )}
                      </td>
                      <td className="text-center">
                        {isAdminHost(item) ? "--" : (
                          <div className="d-flex justify-content-center align-items-center gap-2">
                            {item.adminId ? (
                              <button
                                type="button"
                                title="Edit Tenant"
                                className="bg-success-focus text-success-600 bg-hover-success-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle border-0"
                                onClick={() => openEditModal(item)}
                              >
                                <Icon icon="lucide:edit" className="text-lg" />
                              </button>
                            ) : (
                              <span className="text-secondary-light text-xs fw-medium">Master (Read Only)</span>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={9} className="text-center py-24 text-secondary-light">
                      No White Labels found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* CREATE MODAL */}
      {isCreateOpen && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(15, 23, 42, 0.6)", backdropFilter: "blur(4px)" }}
        >
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content border-0 radius-12 shadow-lg">
              <div className="modal-header border-bottom bg-light px-24 py-16">
                <h5 className="modal-title fw-semibold text-secondary-light">Add New White Label Tenant</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setIsCreateOpen(false)}
                />
              </div>
              <form onSubmit={handleCreateSubmit} className="white-label-modal">
                <style>{`
                  .white-label-modal input::placeholder {
                    opacity: 0.5 !important;
                  }
                `}</style>
                <div className="modal-body px-24 py-20" style={{ maxHeight: "70vh", overflowY: "auto" }}>
                  {/* Domain & Tenant Configuration */}
                  <h6 className="text-primary-600 fw-semibold mb-12 border-bottom pb-8">Tenant & Domain Config</h6>
                  <div className="row g-3 mb-20">
                    <div className="col-md-6">
                      <label className="form-label fw-medium text-secondary-light">
                        Subdomain Prefix <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="e.g. abc"
                        value={createForm.subdomain || ""}
                        onChange={(e) => {
                          const val = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "");
                          let adminD = "", brokerD = "", userD = "";
                          if (val) {
                            const injectSub = (base, sub) => base.replace(/^(https?:\/\/)?/, `$1${sub}.`);
                            adminD = injectSub(process.env.REACT_APP_BASE_ADMIN_DOMAIN || "localhost:5000", val);
                            brokerD = injectSub(process.env.REACT_APP_BASE_BROKER_DOMAIN || "localhost:5002", val);
                            userD = injectSub(process.env.REACT_APP_BASE_USER_DOMAIN || "localhost:5003", val);
                          }

                          setCreateForm({
                            ...createForm,
                            subdomain: val,
                            adminDomain: adminD,
                            brokerDomain: brokerD,
                            userDomain: userD
                          });
                        }}
                        required
                      />
                      {createForm.subdomain && (
                        <div className="mt-2 p-3 bg-neutral-50 radius-8 border border-neutral-200">
                          <small className="d-block text-secondary-light fw-medium mb-1">Generated Domains:</small>
                          <small className="d-block text-neutral-600 mb-1"><strong>Admin:</strong> {createForm.adminDomain}</small>
                          <small className="d-block text-neutral-600 mb-1"><strong>Broker:</strong> {createForm.brokerDomain}</small>
                          <small className="d-block text-neutral-600"><strong>User:</strong> {createForm.userDomain}</small>
                        </div>
                      )}
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-medium text-secondary-light">App Name <span className="text-danger">*</span></label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="e.g. ProTrade Premium"
                        value={createForm.appName}
                        onChange={(e) => setCreateForm({ ...createForm, appName: e.target.value })}
                        required
                      />
                    </div>
                  </div>

                  {/* Brand Branding assets */}
                  <h6 className="text-primary-600 fw-semibold mb-12 border-bottom pb-8">Branding Assets</h6>
                  <div className="row g-3 mb-20">
                    <div className="col-md-6">
                      <label className="form-label fw-medium text-secondary-light">Standard Logo</label>
                      <input
                        type="file"
                        accept="image/*"
                        className="form-control"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) setCreateForm({ ...createForm, standardLogo: file });
                        }}
                      />
                      {createForm.standardLogo && (
                        <img src={URL.createObjectURL(createForm.standardLogo)} alt="Standard Logo" className="mt-2 radius-8" style={{ maxHeight: '48px', objectFit: 'contain' }} />
                      )}
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-medium text-secondary-light">White Logo</label>
                      <input
                        type="file"
                        accept="image/*"
                        className="form-control"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) setCreateForm({ ...createForm, whiteLogo: file });
                        }}
                      />
                      {createForm.whiteLogo && (
                        <img src={URL.createObjectURL(createForm.whiteLogo)} alt="White Logo" className="mt-2 radius-8 bg-dark p-1" style={{ maxHeight: '48px', objectFit: 'contain' }} />
                      )}
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-medium text-secondary-light">Favicon</label>
                      <input
                        type="file"
                        accept="image/*"
                        className="form-control"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) setCreateForm({ ...createForm, favicon: file });
                        }}
                      />
                      {createForm.favicon && (
                        <img src={URL.createObjectURL(createForm.favicon)} alt="Favicon" className="mt-2 radius-8" style={{ maxHeight: '32px', objectFit: 'contain' }} />
                      )}
                    </div>
                    {/* <div className="col-md-6">
                      <label className="form-label fw-medium text-secondary-light">Primary Color</label>
                      <div className="d-flex gap-2">
                        <input
                          type="color"
                          className="form-control form-control-color"
                          style={{ width: "50px", height: "40px", padding: "4px" }}
                          value={createForm.primaryColor}
                          onChange={(e) => setCreateForm({ ...createForm, primaryColor: e.target.value })}
                        />
                        <input
                          type="text"
                          className="form-control font-monospace"
                          placeholder="#0064FF"
                          value={createForm.primaryColor}
                          onChange={(e) => setCreateForm({ ...createForm, primaryColor: e.target.value })}
                        />
                      </div>
                    </div> */}
                  </div>

                  {/* Master Admin credentials */}
                  <h6 className="text-primary-600 fw-semibold mb-12 border-bottom pb-8">Master Tenant Admin credentials</h6>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label fw-medium text-secondary-light">
                        Admin Name <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Master Admin Full Name"
                        value={createForm.adminName}
                        onChange={(e) => setCreateForm({ ...createForm, adminName: e.target.value })}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-medium text-secondary-light">
                        Admin Email <span className="text-danger">*</span>
                      </label>
                      <input
                        type="email"
                        className="form-control"
                        placeholder="admin@brokerdomain.com"
                        value={createForm.adminEmail}
                        onChange={(e) => setCreateForm({ ...createForm, adminEmail: e.target.value })}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-medium text-secondary-light">
                        Admin Password <span className="text-danger">*</span>
                      </label>
                      <div className="input-group">
                        <input
                          type={showCreatePassword ? "text" : "password"}
                          className="form-control"
                          placeholder="Strong Password"
                          value={createForm.adminPassword}
                          onChange={(e) => setCreateForm({ ...createForm, adminPassword: e.target.value })}
                          required
                        />
                        <button type="button" className="input-group-text bg-base" onClick={() => setShowCreatePassword(!showCreatePassword)}>
                          <Icon icon={showCreatePassword ? "tabler:eye-off" : "tabler:eye"} />
                        </button>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-medium text-secondary-light">Admin Contact Number</label>
                      <input
                        type="text"
                        inputMode="numeric"
                        className="form-control"
                        placeholder="e.g. 9876543210"
                        value={createForm.adminContactNumber}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, "").slice(0, 10);
                          setCreateForm({ ...createForm, adminContactNumber: value });
                        }}
                        maxLength={10}
                      />
                    </div>
                  </div>
                </div>
                <div className="modal-footer border-top bg-light px-24 py-12 d-flex justify-content-end gap-2">
                  <button
                    type="button"
                    className="btn btn-secondary-600 radius-8"
                    onClick={() => setIsCreateOpen(false)}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary radius-8">
                    Create Tenant
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* EDIT MODAL */}
      {isEditOpen && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(15, 23, 42, 0.6)", backdropFilter: "blur(4px)" }}
        >
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content border-0 radius-12 shadow-lg">
              <div className="modal-header border-bottom bg-light px-24 py-16">
                <h5 className="modal-title fw-semibold text-secondary-light">Edit White Label Tenant</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setIsEditOpen(false)}
                />
              </div>
              <form onSubmit={handleEditSubmit} className="white-label-modal">
                <style>{`
                  .white-label-modal input::placeholder {
                    opacity: 0.5 !important;
                  }
                `}</style>
                <div className="modal-body px-24 py-20" style={{ maxHeight: "70vh", overflowY: "auto" }}>
                  <h6 className="text-primary-600 fw-semibold mb-12 border-bottom pb-8">Tenant & Domain Config</h6>
                  <div className="row g-3 mb-20">
                    <div className="col-md-6">
                      <label className="form-label fw-medium text-secondary-light">
                        Admin Domain <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        value={editForm.adminDomain}
                        onChange={(e) => setEditForm({ ...editForm, adminDomain: e.target.value })}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-medium text-secondary-light">
                        Broker Domain <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        value={editForm.brokerDomain}
                        onChange={(e) => setEditForm({ ...editForm, brokerDomain: e.target.value })}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-medium text-secondary-light">
                        User Domain <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        value={editForm.userDomain}
                        onChange={(e) => setEditForm({ ...editForm, userDomain: e.target.value })}
                        required
                      />
                    </div>
                  </div>

                  {!editForm.isMaster && (
                    <div className="row g-3 mb-16 border-bottom pb-16">
                      <div className="col-12">
                        <h6 className="text-primary-600 fw-semibold mb-0">Tenant Admin Credentials</h6>
                      </div>
                      <div className="col-md-6">
                        <label className="form-label fw-medium text-secondary-light">Admin Name</label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="e.g. John Doe"
                          value={editForm.adminName}
                          onChange={(e) => setEditForm({ ...editForm, adminName: e.target.value })}
                          required
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label fw-medium text-secondary-light">Admin Email</label>
                        <input
                          type="email"
                          className="form-control bg-base text-secondary"
                          placeholder="e.g. john@domain.com"
                          value={editForm.adminEmail}
                          disabled
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label fw-medium text-secondary-light">Contact Number</label>
                        <input
                          type="text"
                          inputMode="numeric"
                          className="form-control"
                          placeholder="e.g. 9876543210"
                          value={editForm.adminContactNumber}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, "").slice(0, 10);
                            setEditForm({ ...editForm, adminContactNumber: value });
                          }}
                          maxLength={10}
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label fw-medium text-secondary-light">New Password</label>
                        <div className="input-group">
                          <input
                            type={showEditPassword ? "text" : "password"}
                            className="form-control"
                            placeholder="Leave blank to keep current"
                            value={editForm.adminPassword}
                            onChange={(e) => setEditForm({ ...editForm, adminPassword: e.target.value })}
                          />
                          <button type="button" className="input-group-text bg-base" onClick={() => setShowEditPassword(!showEditPassword)}>
                            <Icon icon={showEditPassword ? "tabler:eye-off" : "tabler:eye"} />
                          </button>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <label className="form-label fw-medium text-secondary-light">Confirm Password</label>
                        <div className="input-group">
                          <input
                            type={showEditConfirmPassword ? "text" : "password"}
                            className="form-control"
                            placeholder="Confirm new password"
                            value={editForm.confirmAdminPassword}
                            onChange={(e) => setEditForm({ ...editForm, confirmAdminPassword: e.target.value })}
                          />
                          <button type="button" className="input-group-text bg-base" onClick={() => setShowEditConfirmPassword(!showEditConfirmPassword)}>
                            <Icon icon={showEditConfirmPassword ? "tabler:eye-off" : "tabler:eye"} />
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="row g-3 mb-16">
                    <div className="col-12">
                      <label className="form-label fw-medium text-secondary-light">
                        App Name <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        value={editForm.appName}
                        onChange={(e) => setEditForm({ ...editForm, appName: e.target.value })}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-medium text-secondary-light">Standard Logo</label>
                      <input
                        type="file"
                        accept="image/*"
                        className="form-control"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) setEditForm({ ...editForm, standardLogo: file });
                        }}
                      />
                      <div className="mt-2">
                        {editForm.standardLogo ? (
                          <img src={URL.createObjectURL(editForm.standardLogo)} alt="Standard Logo" className="radius-8" style={{ maxHeight: '48px', objectFit: 'contain' }} />
                        ) : editForm.standardLogoUrl ? (
                          <img src={resolveImageUrl(editForm.standardLogoUrl)} alt="Current Standard Logo" className="radius-8" style={{ maxHeight: '48px', objectFit: 'contain' }} />
                        ) : null}
                      </div>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-medium text-secondary-light">White Logo</label>
                      <input
                        type="file"
                        accept="image/*"
                        className="form-control"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) setEditForm({ ...editForm, whiteLogo: file });
                        }}
                      />
                      <div className="mt-2">
                        {editForm.whiteLogo ? (
                          <img src={URL.createObjectURL(editForm.whiteLogo)} alt="White Logo" className="radius-8 bg-dark p-1" style={{ maxHeight: '48px', objectFit: 'contain' }} />
                        ) : editForm.whiteLogoUrl ? (
                          <img src={resolveImageUrl(editForm.whiteLogoUrl)} alt="Current White Logo" className="radius-8 bg-dark p-1" style={{ maxHeight: '48px', objectFit: 'contain' }} />
                        ) : null}
                      </div>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-medium text-secondary-light">Favicon</label>
                      <input
                        type="file"
                        accept="image/*"
                        className="form-control"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) setEditForm({ ...editForm, favicon: file });
                        }}
                      />
                      <div className="mt-2">
                        {editForm.favicon ? (
                          <img src={URL.createObjectURL(editForm.favicon)} alt="Favicon" className="radius-8" style={{ maxHeight: '32px', objectFit: 'contain' }} />
                        ) : editForm.faviconUrl ? (
                          <img src={resolveImageUrl(editForm.faviconUrl)} alt="Current Favicon" className="radius-8" style={{ maxHeight: '32px', objectFit: 'contain' }} />
                        ) : null}
                      </div>
                    </div>
                    {/* <div className="col-md-6">
                      <label className="form-label fw-medium text-secondary-light">Primary Color</label>
                      <div className="d-flex gap-2">
                        <input
                          type="color"
                          className="form-control form-control-color"
                          style={{ width: "50px", height: "40px", padding: "4px" }}
                          value={editForm.primaryColor}
                          onChange={(e) => setEditForm({ ...editForm, primaryColor: e.target.value })}
                        />
                        <input
                          type="text"
                          className="form-control font-monospace"
                          value={editForm.primaryColor}
                          onChange={(e) => setEditForm({ ...editForm, primaryColor: e.target.value })}
                        />
                      </div>
                    </div> */}
                  </div>
                </div>
                <div className="modal-footer border-top bg-light px-24 py-12 d-flex justify-content-end gap-2">
                  <button
                    type="button"
                    className="btn btn-secondary-600 radius-8"
                    onClick={() => setIsEditOpen(false)}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary radius-8">
                    Save Changes
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default WhiteLabelsLayer;
